import { appConfig, storageKeys } from '@/config/app';
import { getOrCreateSessionId, getOrCreateVisitorId } from '@/analytics/attribution';
import { reportApiError } from '@/analytics/health';
import { local } from '@/lib/storage';
import { ApiError, rateLimitMessage } from './errors';
import { LiveTransport, resolveApiBaseUrl } from './live';
import { MockTransport } from './mock/transport';
import type { ApiTransport, HttpMethod } from './transport';

/** Отказы самой телеметрии не репортим — иначе недоступный приёмник порождал бы поток api_error о себе. */
const TELEMETRY_ENDPOINTS = [
  '/landing-event',
  '/landing-events/batch',
  '/guest-visit',
  '/heartbeat',
  '/search/click',
];

/**
 * Путь без query и без идентификаторов: `/canvases/12/nodes/ab12…` →
 * `/canvases/:id/nodes/:token`. В аналитику не попадает ничего, по чему
 * восстанавливается сущность или пользователь.
 */
export function toEndpointPattern(endpoint: string): string {
  const path = endpoint.split('?')[0] ?? '';
  return path
    .split('/')
    .map((segment) => {
      if (/^\d+$/.test(segment)) return ':id';
      if (segment.length >= 20) return ':token';
      return segment;
    })
    .join('/');
}

function readStoredUserId(): number | null {
  const parsed = local.getJson<{ id?: unknown }>(storageKeys.user);
  return typeof parsed?.id === 'number' ? parsed.id : null;
}

export interface RequestOptions {
  headers?: Record<string, string>;
  keepalive?: boolean;
  signal?: AbortSignal;
}

/**
 * Единственный HTTP-слой приложения (TRK-1): Bearer из `shotbook_token`,
 * заголовки идентичности на каждом запросе, 401 → принудительный выход,
 * 429 → человекочитаемое сообщение, ошибки с status/code/data, событие
 * `api_error` с обезличенным путём.
 */
export class ApiClient {
  constructor(private readonly transport: ApiTransport) {}

  private identityHeaders(): Record<string, string> {
    try {
      const headers: Record<string, string> = {};
      const visitorId = getOrCreateVisitorId();
      const sessionId = getOrCreateSessionId();
      if (visitorId) headers['X-Visitor-Id'] = visitorId;
      if (sessionId) headers['X-Session-Id'] = sessionId;
      return headers;
    } catch {
      return {};
    }
  }

  private authHeader(): Record<string, string> {
    const token = local.get(storageKeys.token);
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  private async request<T>(
    method: HttpMethod,
    endpoint: string,
    body?: unknown,
    options: RequestOptions = {},
  ): Promise<T> {
    const startedAt = Date.now();
    // FormData (загрузка файла) уходит без Content-Type: границу multipart ставит браузер.
    const isForm = typeof FormData !== 'undefined' && body instanceof FormData;
    const headers: Record<string, string> = {
      ...(isForm ? {} : { 'Content-Type': 'application/json' }),
      Accept: 'application/json',
      ...this.identityHeaders(),
      ...this.authHeader(),
      ...options.headers,
    };

    let response;
    try {
      response = await this.transport.send({
        method,
        path: endpoint,
        body,
        headers,
        keepalive: options.keepalive,
        signal: options.signal,
      });
    } catch (error) {
      // Отмена — не отказ сети: вызывающий сам снял запрос, событие api_error не нужно.
      if (options.signal?.aborted) throw error;
      // Сетевой отказ (DNS, offline, CORS) — статуса нет, помечаем нулём.
      this.report(endpoint, method, 0, startedAt);
      throw new ApiError(error instanceof Error ? error.message : 'Network error', 0);
    }

    const { status } = response;
    const data = (response.body ?? null) as { message?: string; error?: string } | null;

    if (status >= 200 && status < 300) {
      return (data ?? {}) as T;
    }

    this.report(endpoint, method, status, startedAt);

    if (status === 401) {
      const userId = readStoredUserId();
      local.remove(storageKeys.token);
      window.dispatchEvent(new CustomEvent('auth:logout', { detail: { user_id: userId } }));
      throw new ApiError(data?.message || 'Unauthorized', 401, data?.error, data);
    }

    if (status === 429) {
      throw new ApiError(rateLimitMessage(), 429, data?.error, data);
    }

    throw new ApiError(
      data?.message || data?.error || `API error ${status}`,
      status,
      data?.error,
      data,
    );
  }

  private report(endpoint: string, method: string, status: number, startedAt: number): void {
    const pattern = toEndpointPattern(endpoint);
    if (TELEMETRY_ENDPOINTS.some((telemetry) => pattern.startsWith(telemetry))) return;
    reportApiError({
      endpoint_pattern: pattern,
      method,
      status,
      duration_ms: Date.now() - startedAt,
    });
  }

  get<T>(endpoint: string, options?: RequestOptions): Promise<T> {
    return this.request<T>('GET', endpoint, undefined, options);
  }

  post<T>(endpoint: string, body: unknown = {}, options?: RequestOptions): Promise<T> {
    return this.request<T>('POST', endpoint, body, options);
  }

  put<T>(endpoint: string, body: unknown = {}, options?: RequestOptions): Promise<T> {
    return this.request<T>('PUT', endpoint, body, options);
  }

  patch<T>(endpoint: string, body: unknown = {}, options?: RequestOptions): Promise<T> {
    return this.request<T>('PATCH', endpoint, body, options);
  }

  delete<T>(endpoint: string, options?: RequestOptions): Promise<T> {
    return this.request<T>('DELETE', endpoint, undefined, options);
  }

  /**
   * Fire-and-forget без заголовков: sendBeacon, фолбэк — fetch keepalive.
   * Bearer не передаётся — только для событий, где достаточно visitor_id.
   */
  beacon(endpoint: string, payload: Record<string, unknown>): void {
    if (this.transport.beacon(endpoint, payload)) return;
    this.postKeepalive(endpoint, payload, false);
  }

  /** Доставка с Bearer, переживающая уход со страницы (web_vital и т.п.). */
  postKeepalive(endpoint: string, payload: Record<string, unknown>, withAuth = true): void {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...this.identityHeaders(),
      ...(withAuth ? this.authHeader() : {}),
    };
    this.transport
      .send({ method: 'POST', path: endpoint, body: payload, headers, keepalive: true })
      .catch(() => {
        // Телеметрия не ломает UX.
      });
  }
}

function createTransport(): ApiTransport {
  if (appConfig.apiMode === 'mock') return new MockTransport();
  const base = resolveApiBaseUrl(
    appConfig.apiUrl,
    window.location.hostname,
    window.location.origin,
  );
  if (import.meta.env.DEV) console.info('[api] live base URL:', base);
  return new LiveTransport(base);
}

export const api = new ApiClient(createTransport());
